import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';

const ProgressRingCard = ({
  title,
  value,
  previousValue,
  unit = '',
  color = 'blue',
  summary = '',
  size = 'medium',
}) => {
  const { t, isHindi } = useTranslation('dashboard');
  const percentage = previousValue > 0 ? Math.min(100, (value / previousValue) * 100) : 100;
  const growth = previousValue > 0 ? ((value - previousValue) / previousValue) * 100 : 0;
  const isPositive = growth >= 0;

  // Format numbers
  const formatNumber = (num, isCurrency = false) => {
    if (typeof num !== 'number' || isNaN(num)) return '-';
    const locale = isHindi ? 'hi-IN' : 'en-IN';
    if (isCurrency) {
      if (num >= 1e7) return `₹${(num / 1e7).toFixed(2)}Cr`;
      if (num >= 1e5) return `₹${(num / 1e5).toFixed(2)}L`;
      if (num >= 1e3) return `₹${(num / 1e3).toFixed(2)}K`;
      return `₹${num.toLocaleString(locale, { maximumFractionDigits: 2 })}`;
    }
    if (num >= 1e7) return `${(num / 1e7).toFixed(2)}Cr`;
    if (num >= 1e5) return `${(num / 1e5).toFixed(2)}L`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toLocaleString(locale, { maximumFractionDigits: 2 });
  };

  const ringSize = size === 'large' ? 120 : size === 'medium' ? 100 : 80;
  const strokeWidth = size === 'large' ? 8 : size === 'medium' ? 6 : 4;
  const radius = (ringSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const colors = {
    blue: { primary: '#2563eb', secondary: '#dbeafe', accent: '#1d4ed8' },
    green: { primary: '#22c55e', secondary: '#dcfce7', accent: '#16a34a' },
    purple: { primary: '#8b5cf6', secondary: '#ede9fe', accent: '#7c3aed' }
  };
  const selectedColor = colors[color];

  // Summary sentence
  let summaryText = summary;
  if (!summaryText) {
    if (growth === 0) summaryText = t('summaries.noChangeFromLastMonth');
    else if (isPositive) summaryText = `${Math.abs(growth).toFixed(1)}% ${t('summaries.higherRevenueThanLastMonth')}`;
    else summaryText = `${Math.abs(growth).toFixed(1)}% ${t('summaries.lessRevenueThanLastMonth')}`;
  }

  return (
    <div className="progress-ring-card">
      <div className="progress-ring-header">
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{title}</span>
        <div className={`trend-indicator ${isPositive ? 'positive' : 'negative'}`}>{isPositive ? '↗' : '↘'} {Math.abs(growth).toFixed(1)}%</div>
      </div>
      <div className="progress-ring-container">
        <svg width={ringSize} height={ringSize} className="progress-ring">
          {/* Background ring */}
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={radius}
            stroke={selectedColor.secondary}
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* Progress ring */}
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={radius}
            stroke={selectedColor.primary}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
            className="progress-ring-fill"
          />
        </svg>
        <div className="progress-ring-content">
          <div className="main-value">
            {formatNumber(value, unit === '₹')}
          </div>
          <div className="comparison">
            vs {formatNumber(previousValue, unit === '₹')}
          </div>
        </div>
      </div>
      <div className="progress-ring-summary">{summaryText}</div>
    </div>
  );
};

export default ProgressRingCard; 